import { createContext, useContext, type ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import type { ActivePromotionDTO } from "@biosany/shared"
import { api } from "@/lib/api"

const ActivePromotionsContext = createContext<ActivePromotionDTO[]>([])

export function ActivePromotionProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const { data } = useQuery({
		queryKey: ["active-promotions"],
		queryFn: () => api.getActivePromotions(),
		staleTime: 60_000,
		retry: false
	})

	return <ActivePromotionsContext.Provider value={data?.promotions ?? []}>{children}</ActivePromotionsContext.Provider>
}

export function useActivePromotions(): ActivePromotionDTO[] {
	return useContext(ActivePromotionsContext)
}

/** Promo que aplica a un ítem de catálogo, o null. */
export function useActivePromotionForItem(itemId: string | undefined): ActivePromotionDTO | null {
	const promotions = useActivePromotions()
	if (!itemId) return null
	return promotions.find(p => p.itemIds.includes(itemId)) ?? null
}

/**
 * Una promo “representativa” para pantallas que aún esperan un solo DTO
 * (p. ej. listados). Prefiere la que incluya `preferredItemId` si se indica.
 */
export function useActivePromotion(preferredItemId?: string): ActivePromotionDTO | null {
	const promotions = useActivePromotions()
	if (preferredItemId) {
		return promotions.find(p => p.itemIds.includes(preferredItemId)) ?? null
	}
	return promotions[0] ?? null
}

export function promotionForItem(promotions: ActivePromotionDTO[], itemId: string): ActivePromotionDTO | null {
	return promotions.find(p => p.itemIds.includes(itemId)) ?? null
}

/**
 * Ítems del carrito con envío gratis por alguna promo activa.
 * Se consideran todas las promos (no solo la del cupón Stripe).
 */
export function freeShippingItemIdsForCart(promotions: ActivePromotionDTO[], cartItemIds: string[]): string[] {
	const ids = new Set<string>()
	for (const promo of promotions) {
		if (!promo.freeShipping) continue
		for (const itemId of promo.itemIds) {
			if (cartItemIds.includes(itemId)) ids.add(itemId)
		}
	}
	return [...ids]
}
